import { useCallback, useEffect, useState } from "react";
import { useUrlParams } from "./use-url-params";
import { useUrlSetter } from "./use-url-sync";

export type WhenValue = Date | { startDate: string; endDate: string; label: string };

/**
 * Convert Date object to YYYY-MM-DD string
 */
const dateToUrlString = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Convert YYYY-MM-DD string to Date object
 */
const urlStringToDate = (dateString: string): Date => {
  return new Date(dateString + 'T00:00:00');
};

/**
 * Check if a date string is valid
 */
const isValidDateString = (dateString: string): boolean => {
  const date = new Date(dateString + 'T00:00:00');
  return !isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};

/**
 * Convert when value to URL parameter string
 */
const whenValueToUrl = (when: WhenValue): string => {
  if (when instanceof Date) {
    return dateToUrlString(when);
  } else {
    // Date range object
    return `${when.startDate}:${when.endDate}`;
  }
};

/**
 * Convert URL parameter string to when value
 */
const urlToWhenValue = (urlValue: string): WhenValue | undefined => {
  if (!urlValue) return undefined;

  // Check if it's a date range (contains colon)
  if (urlValue.includes(':')) {
    const [startDate, endDate] = urlValue.split(':');
    
    if (isValidDateString(startDate) && isValidDateString(endDate)) {
      // Validate that end date is after start date
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      
      if (end >= start) {
        // Determine label based on the date range
        const today = new Date();
        const currentDay = today.getDay(); // 0 for Sunday, 1 for Monday, ..., 6 for Saturday
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        const isThisWeek = dateToUrlString(start) === dateToUrlString(startOfWeek) && 
                          dateToUrlString(end) === dateToUrlString(endOfWeek);

        // Create appropriate label
        let label: string;
        if (isThisWeek) {
          label = "This Week";
        } else if (startDate === endDate) {
          // Single date - format nicely
          label = urlStringToDate(startDate).toLocaleDateString();
        } else {
          // Date range
          label = `${urlStringToDate(startDate).toLocaleDateString()} - ${urlStringToDate(endDate).toLocaleDateString()}`;
        }

        return {
          startDate,
          endDate,
          label
        };
      }
    }
    return undefined; // Invalid date range
  } else {
    // Single date
    if (isValidDateString(urlValue)) {
      return urlStringToDate(urlValue);
    }
    return undefined; // Invalid date
  }
};

/**
 * Hook to manage when filter synchronization with URL parameters
 */
export const useWhenUrl = () => {
  const { getParam, setParam, removeParam } = useUrlParams();
  const [currentWhen, setCurrentWhen] = useState<WhenValue | undefined>();

  // Initialize when value from URL on mount
  useEffect(() => {
    const urlWhen = getParam('when');
    const whenValue = urlWhen ? urlToWhenValue(urlWhen) : undefined;
    setCurrentWhen(whenValue);
  }, [getParam]);

  /**
   * Update when value and sync with URL
   */
  const syncWhenToUrl = useUrlSetter<WhenValue>(
    'when',
    setParam,
    removeParam,
    whenValueToUrl,
  );

  const setWhen = useCallback((when: WhenValue | undefined) => {
    setCurrentWhen(when);
    syncWhenToUrl(when);
  }, [syncWhenToUrl]);

  return {
    currentWhen,
    setWhen,
  };
};